/**
 * AcceptInvitation — public page at /activar?token=...
 *
 * Flow:
 *   1. On mount, calls GET /auth/invitation/:token/validate to confirm the
 *      token is valid and to fetch the investor's email + name + preferred
 *      language. Friendly error if expired / used / not-found.
 *   2. Shows a brief welcome with the investor's name, then a single
 *      "create your password" form (new + confirm).
 *   3. Submits to POST /auth/accept-invitation. Backend sets auth cookies
 *      on success, so we hit `refreshMe()` from useAuth and redirect to "/".
 *
 * Language: pulls user.language from validate response (Spanish default).
 * No translation library — small, two-language strings inline.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LockKey, ArrowRight } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Hero background — real Dolce Villas render (WebP, ~160 KB, 1600×1600).
// Mirrors Login.jsx so both public screens share one brand surface. The
// gradient stays underneath as fallback: if the image 404s the browser
// silently drops that layer and the terracotta/dark gradient still renders.
const HERO_IMG = "/brand/dolce-villas-hero.webp";
const HERO_FALLBACK_GRADIENT = `
  radial-gradient(ellipse at 78% 18%, rgba(232,166,131,0.42), transparent 58%),
  radial-gradient(ellipse at 18% 88%, rgba(156,82,54,0.32), transparent 60%),
  linear-gradient(140deg, #1a1411 0%, #2a1c16 45%, #3a2419 100%)
`;
// Overlay above the photo for text legibility. 55% dark-warm mix per design
// decision — lower the first stop toward 0.45 if staging shows too dark.
const HERO_OVERLAY = `
  linear-gradient(125deg, rgba(14,13,12,0.55), rgba(26,20,17,0.35) 60%, rgba(156,82,54,0.20))
`;

const COPY = {
  es: {
    title:           "Activar mi cuenta",
    welcome:         "Hola {name}, bienvenido a Dolce Villas Platform.",
    intro:           "Define una contraseña para acceder a tu portal privado.",
    password_label:  "Nueva contraseña",
    password_hint:   "Mínimo 8 caracteres.",
    confirm_label:   "Confirmar contraseña",
    submit:          "Activar mi cuenta",
    submitting:      "Activando…",
    no_match:        "Las contraseñas no coinciden.",
    too_short:       "La contraseña debe tener al menos 8 caracteres.",
    invalid_link:    "Este enlace no es válido o ha expirado.",
    contact_admin:   "Contacta con el administrador para solicitar uno nuevo.",
    go_login:        "Ir al inicio de sesión",
    loading:         "Verificando enlace…",
    hero_kicker:     "Área privada",
    hero_tagline_1:  "Donde el lujo se convierte en",
    hero_tagline_2:  "rentabilidad",
    hero_sub:        "Accede a tu portal y empieza a seguir la evolución de tu villa, paso a paso.",
  },
  en: {
    title:           "Activate my account",
    welcome:         "Hi {name}, welcome to Dolce Villas Platform.",
    intro:           "Set a password to access your private portal.",
    password_label:  "New password",
    password_hint:   "At least 8 characters.",
    confirm_label:   "Confirm password",
    submit:          "Activate my account",
    submitting:      "Activating…",
    no_match:        "Passwords do not match.",
    too_short:       "Password must be at least 8 characters.",
    invalid_link:    "This link is invalid or has expired.",
    contact_admin:   "Please contact the administrator for a new one.",
    go_login:        "Go to login",
    loading:         "Verifying link…",
    hero_kicker:     "Private area",
    hero_tagline_1:  "Where luxury becomes",
    hero_tagline_2:  "return",
    hero_sub:        "Step into your portal and start following your villa, step by step.",
  },
};

function pick(lang) { return COPY[lang === "en" ? "en" : "es"]; }

export default function AcceptInvitation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const token = params.get("token") || "";

  const [state, setState] = useState({ phase: "validating", info: null, error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  const lang = state.info?.language === "en" ? "en" : "es";
  const t = useMemo(() => pick(lang), [lang]);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ phase: "invalid", info: null, error: pick("es").invalid_link });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/invitation/${encodeURIComponent(token)}/validate`);
        if (!cancelled) setState({ phase: "ready", info: data, error: "" });
      } catch (e) {
        if (!cancelled) {
          setState({
            phase: "invalid",
            info: null,
            error: formatError(e) || pick("es").invalid_link,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (password.length < 8) { setSubmitErr(t.too_short); return; }
    if (password !== confirm) { setSubmitErr(t.no_match); return; }
    setBusy(true);
    try {
      await api.post("/auth/accept-invitation", { token, new_password: password });
      // Backend set cookies; refresh auth context and redirect to portal.
      try { await refreshMe(); } catch { /* noop */ }
      navigate("/", { replace: true });
    } catch (err) {
      setSubmitErr(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (state.phase === "validating") {
    return (
      <PremiumShell t={t}>
        <div className="text-white/60 text-sm" data-testid="accept-invitation-loading">
          {t.loading}
        </div>
      </PremiumShell>
    );
  }

  if (state.phase === "invalid") {
    const tt = pick("es");
    return (
      <PremiumShell t={tt}>
        <div className="w-full max-w-md text-center" data-testid="accept-invitation-invalid">
          <div className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: "#E8A683" }}>
            Dolce Villas
          </div>
          <div className="font-serif-dv text-3xl text-white mb-3">{tt.invalid_link}</div>
          <div className="text-sm text-white/60 mb-6">{state.error || tt.contact_admin}</div>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md font-medium text-white transition-all hover:-translate-y-0.5"
            style={{ background: "#9C5236", boxShadow: "0 10px 30px -10px rgba(156,82,54,0.55)" }}
          >
            {tt.go_login}
            <ArrowRight size={18} />
          </Link>
        </div>
      </PremiumShell>
    );
  }

  const name = state.info?.name || state.info?.email || "";

  return (
    <PremiumShell t={t}>
      <div className="w-full max-w-md fade-up" data-testid="accept-invitation-form">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-8" style={{ background: "#9C5236" }} />
          <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#E8A683" }}>
            {t.hero_kicker}
          </div>
        </div>
        <h1 className="font-serif-dv text-4xl text-white mb-2">{t.title}</h1>
        <p className="text-white/75 mb-1">{t.welcome.replace("{name}", name)}</p>
        <p className="text-sm text-white/50 mb-8">{t.intro}</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50">{t.password_label}</label>
            <DarkInput
              type="password"
              value={password}
              onChange={setPassword}
              minLength={8}
              required
              autoFocus
              tid="accept-invitation-password"
            />
            <div className="text-xs text-white/40 mt-1">{t.password_hint}</div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-white/50">{t.confirm_label}</label>
            <DarkInput
              type="password"
              value={confirm}
              onChange={setConfirm}
              minLength={8}
              required
              tid="accept-invitation-confirm"
            />
          </div>
          {submitErr && (
            <div
              className="text-sm px-3 py-2 rounded-md"
              style={{
                background: "rgba(156,82,54,0.15)",
                color: "#E8A683",
                border: "1px solid rgba(156,82,54,0.35)",
              }}
              data-testid="accept-invitation-error"
            >
              {submitErr}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="accept-invitation-submit"
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-md font-medium text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
            style={{
              background: "#9C5236",
              boxShadow: "0 10px 30px -10px rgba(156,82,54,0.55)",
            }}
          >
            {busy ? t.submitting : t.submit}
            <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </PremiumShell>
  );
}

// PremiumShell — 2-column brand surface used by all three phases of the
// activation flow. Mirrors the structural pattern of Login.jsx so both
// public screens share the same visual identity. Mobile hides the hero and
// renders the form full-width.
function PremiumShell({ children, t }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" style={{ background: "#0E0D0C" }}>
      {/* LEFT — Brand gradient hero (stopgap until official asset) */}
      <div
        className="relative hidden lg:block"
        style={{
          backgroundImage: `${HERO_OVERLAY}, url('${HERO_IMG}'), ${HERO_FALLBACK_GRADIENT}`,
          backgroundSize: "cover, cover, auto",
          backgroundPosition: "center, center, center",
          backgroundRepeat: "no-repeat",
          backgroundColor: "#1a1411",
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top right, rgba(156,82,54,0.25), transparent 55%)" }}
        />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <div className="fade-up flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center font-serif-dv text-xl"
              style={{ background: "#9C5236" }}
            >
              D
            </div>
            <div>
              <div className="font-serif-dv text-3xl tracking-tight leading-none">Dolce Villas</div>
              <div className="text-[10px] tracking-[0.35em] uppercase opacity-60 mt-1">Lombok · Indonesia</div>
            </div>
          </div>

          <div className="max-w-md fade-up-2">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-10" style={{ background: "#9C5236" }} />
              <div className="text-[11px] tracking-[0.3em] uppercase" style={{ color: "#E8A683" }}>
                {t.hero_kicker}
              </div>
            </div>
            <h1 className="font-serif-dv text-5xl xl:text-6xl leading-[1.05] mb-6">
              {t.hero_tagline_1}{" "}
              <em className="not-italic" style={{ color: "#E8A683" }}>{t.hero_tagline_2}</em>.
            </h1>
            <p className="text-white/75 leading-relaxed max-w-sm">{t.hero_sub}</p>
          </div>

          <div className="text-[10px] tracking-[0.3em] uppercase opacity-50">
            © {new Date().getFullYear()} Dolce Villas Lombok
          </div>
        </div>
      </div>

      {/* RIGHT — Form on dark bg with terracotta accents */}
      <div className="flex items-center justify-center p-6 sm:p-12 lg:p-16 relative" style={{ background: "#0E0D0C" }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at bottom left, rgba(156,82,54,0.18), transparent 60%)" }}
        />
        <div className="w-full max-w-md relative z-10 flex flex-col items-center lg:items-start">
          <div className="lg:hidden mb-10 flex items-center gap-3 text-white self-start">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center font-serif-dv text-xl"
              style={{ background: "#9C5236" }}
            >
              D
            </div>
            <div>
              <div className="font-serif-dv text-2xl leading-none">Dolce Villas</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">Lombok · Indonesia</div>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

function DarkInput({ type, value, onChange, required, tid, autoComplete, autoFocus, minLength }) {
  return (
    <div className="relative mt-1">
      <LockKey size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" weight="regular" />
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={tid}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        minLength={minLength}
        className="w-full pl-11 pr-4 py-3 rounded-md text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.12)",
        }}
      />
    </div>
  );
}
